type OrganizationTrialFields = {
  tier?: string | null
  billing_policy?: {
    subscription_required?: boolean | null
  } | null
}

type SubscriptionBillingState = {
  status?: string | null
  subscription_cancellation_scheduled?: boolean | null
  subscription_cancellation_effective_at?: string | Date | null
  subscription_current_period_end?: string | Date | null
}

type OrganizationSubscriptionExpiryInput = {
  billingAvailable: boolean
  subscriptionRequired: boolean
  signupStatus?: string | null
  billing?: SubscriptionBillingState | null
  now?: number
}

type OrganizationSubscriptionRestrictionInput = Omit<
  OrganizationSubscriptionExpiryInput,
  "subscriptionRequired"
> & {
  organization?: OrganizationTrialFields | null
}

export type OrganizationSubscriptionRestriction = {
  active: boolean
  blockedReason: "checkout_required" | "subscription_ended" | null
  appUsableFreeTrial: boolean
}

export type AutomaticLetterDeliveryAccess = {
  allowed: boolean
  disabledReason: string | null
}

export const freeTrialAutomaticLetterDeliveryDisabledReason =
  "Automatische Zustellung ist in der kostenlosen Testphase nicht verfügbar. Nach dem Wechsel auf einen bezahlten Tarif können Terminbriefe automatisch zugestellt werden."

export const organizationRequiresCheckout = (
  organization: OrganizationTrialFields | null | undefined,
): boolean => organization?.billing_policy?.subscription_required !== false

const endedBillingStatuses = new Set(["canceled", "incomplete_expired", "suspended"])

export const isBillingStatusEnded = (status: string | null | undefined): boolean =>
  endedBillingStatuses.has(
    String(status ?? "")
      .trim()
      .toLowerCase(),
  )

export const isOrganizationSubscriptionExpired = ({
  billingAvailable,
  subscriptionRequired,
  signupStatus = null,
  billing = null,
  now = Date.now(),
}: OrganizationSubscriptionExpiryInput): boolean => {
  if (!billingAvailable || !subscriptionRequired) return false

  if (isBillingStatusEnded(signupStatus) || isBillingStatusEnded(billing?.status)) return true

  if (!billing?.subscription_cancellation_scheduled) return false

  const cancellationAt =
    billing.subscription_cancellation_effective_at ?? billing.subscription_current_period_end

  if (!cancellationAt) return false

  const cancellationTimestamp = new Date(cancellationAt).getTime()

  return Number.isFinite(cancellationTimestamp) && cancellationTimestamp <= now
}

export const isFreeTrialExperienceOrganization = (
  organization: OrganizationTrialFields | null | undefined,
): boolean => {
  const tier = String(organization?.tier ?? "")
    .trim()
    .toLowerCase()

  return tier === "free_trial" && !organizationRequiresCheckout(organization)
}

export const resolveOrganizationSubscriptionRestriction = ({
  organization = null,
  billingAvailable,
  signupStatus = null,
  billing = null,
  now = Date.now(),
}: OrganizationSubscriptionRestrictionInput): OrganizationSubscriptionRestriction => {
  const appUsableFreeTrial = isFreeTrialExperienceOrganization(organization)
  const subscriptionRequired = organizationRequiresCheckout(organization)
  const tier = String(organization?.tier ?? "")
    .trim()
    .toLowerCase()

  if (!billingAvailable || appUsableFreeTrial || !subscriptionRequired) {
    return { active: false, blockedReason: null, appUsableFreeTrial }
  }

  if (tier === "free_trial") {
    return { active: true, blockedReason: "checkout_required", appUsableFreeTrial }
  }

  if (
    isOrganizationSubscriptionExpired({
      billingAvailable,
      subscriptionRequired,
      signupStatus,
      billing,
      now,
    })
  ) {
    return { active: true, blockedReason: "subscription_ended", appUsableFreeTrial }
  }

  return { active: false, blockedReason: null, appUsableFreeTrial }
}

/** Tariff policy for every member alike; no administrator or staff exemption exists. */
export const automaticLetterDeliveryAccessForOrganization = (
  organization: OrganizationTrialFields | null | undefined,
): AutomaticLetterDeliveryAccess => {
  const tier = String(organization?.tier ?? "")
    .trim()
    .toLowerCase()

  if (tier === "free_trial") {
    return {
      allowed: false,
      disabledReason: freeTrialAutomaticLetterDeliveryDisabledReason,
    }
  }

  return {
    allowed: true,
    disabledReason: null,
  }
}

export const automaticLetterDeliveryAllowedForOrganization = (
  organization: OrganizationTrialFields | null | undefined,
): boolean => automaticLetterDeliveryAccessForOrganization(organization).allowed

/**
 * Free-trial restrictions bind the organization's own members. Platform staff acting through a
 * checked support delegation keep their exemption; the server applies the same rule.
 */
export const isFreeTrialRestrictionActive = (
  organization: OrganizationTrialFields | null | undefined,
  options: { supportDelegationActive?: boolean } = {},
): boolean => !options.supportDelegationActive && isFreeTrialExperienceOrganization(organization)
